/**
 * The side column's width, dragged and remembered (RFC 0324 3e).
 *
 * 320px by default, a quarter wider than the 256px it was, because a comment
 * with a face and a name needs the room. Kept in the window's storage so it
 * survives a reload and is the same in every pane.
 */

import { useCallback, useState } from 'react'

const KEY = 'mod_side_width'
export const SIDE_DEFAULT = 320
export const SIDE_MIN = 240
export const SIDE_MAX = 640

export function clampSide(px: number): number {
  if (!Number.isFinite(px)) return SIDE_DEFAULT
  return Math.round(Math.min(SIDE_MAX, Math.max(SIDE_MIN, px)))
}

export function readSideWidth(): number {
  try {
    const raw = localStorage.getItem(KEY)
    return raw ? clampSide(Number(raw)) : SIDE_DEFAULT
  } catch {
    return SIDE_DEFAULT
  }
}

/** The width, and a pointer-down handler for a handle on the column's left
 *  edge: dragging left widens it. Saved when the drag ends. */
export function useSideWidth(): {
  width: number
  onHandleDown: (e: React.PointerEvent) => void
  reset: () => void
} {
  const [width, setWidth] = useState(readSideWidth)
  const onHandleDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault()
      const startX = e.clientX
      const startW = width
      let last = startW
      const move = (ev: PointerEvent) => {
        last = clampSide(startW + (startX - ev.clientX))
        setWidth(last)
      }
      const up = () => {
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', up)
        document.body.style.cursor = ''
        try {
          localStorage.setItem(KEY, String(last))
        } catch {
          // No storage: the width lasts this page only.
        }
      }
      document.body.style.cursor = 'col-resize'
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', up)
    },
    [width],
  )
  const reset = useCallback(() => {
    setWidth(SIDE_DEFAULT)
    try {
      localStorage.removeItem(KEY)
    } catch {
      // Nothing saved to remove.
    }
  }, [])
  return { width, onHandleDown, reset }
}
